/**
 * SEO Helper Utilities
 * Contains functions to help with SEO optimization across the application
 */

/**
 * Generates SEO-friendly alt text for images based on the current language
 * Provides fallback if translation key doesn't exist
 * 
 * @param {Function} t - Translation function from useTranslation hook
 * @param {String} baseKey - Base translation key for the image
 * @param {String} fallbackEn - English fallback if translation missing
 * @param {String} fallbackAr - Arabic fallback if translation missing
 * @param {String} currentLang - Current language ('en' or 'ar')
 * @return {String} - Appropriate alt text for the current language
 */
export const getImageAltText = (t, baseKey, fallbackEn, fallbackAr, currentLang = null) => {
  // If translation exists, use it
  const translatedAlt = t(`${baseKey}`, { ns: 'seo' });
  
  // If we got back the key itself, then translation is missing
  if (translatedAlt === baseKey) {
    // Use the appropriate fallback based on language
    const lang = currentLang || (typeof t().language === 'function' ? t().language() : 'en');
    return lang === 'ar' ? fallbackAr : fallbackEn;
  }
  
  return translatedAlt;
};

/**
 * Updates image alt attributes in a component for SEO
 * Should be called in useEffect to update alt text when language changes
 * 
 * @param {String} componentId - ID of the component containing images
 * @param {Function} t - Translation function
 * @param {String} language - Current language code ('en' or 'ar')
 */
export const updateImageAltText = (componentId, t, language) => {
  // Find all images in the component
  const component = document.getElementById(componentId);
  if (!component) return;
  
  const images = component.querySelectorAll('img');
  
  // Update each image's alt attribute based on its data attributes
  images.forEach(img => {
    const altKey = img.dataset.altKey;
    const altEn = img.dataset.altEn;
    const altAr = img.dataset.altAr;
    
    if (altKey) {
      img.alt = getImageAltText(t, altKey, altEn, altAr, language);
    }
  });
};
